import React, { useState, useRef, useEffect } from 'react';
import { auth } from './firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useUserRole } from './useUserRole';
import './AsesorAuto.css';

const functions = getFunctions();
const consultarAsesorAuto = httpsCallable(functions, 'consultarAsesorAuto', { timeout: 110000 });
const consultarClaudePM = httpsCallable(functions, 'consultarClaudePM', { timeout: 280000 });

const MODOS = {
    asesor: {
        label: 'Asesor de compra',
        icono: '🚘',
        titulo: 'Asesor de Compra',
        subtitulo: 'Recomendaciones personalizadas · Nuevos y usados · Valor de reventa',
        emptyTitulo: '¿En qué auto estás pensando?',
        emptySub: 'Cuéntame tu presupuesto, qué tipo de auto buscas y para qué lo usarás. Analizo precios de nuevos y usados para darte la mejor recomendación.',
        placeholder: 'Ej: Tengo $20.000.000 y busco un SUV para familia, ¿qué me recomiendas?',
        sugerencias: [
            'Tengo $25.000.000 y quiero un SUV, ¿qué me recomiendas?',
            'Busco el mejor auto familiar hasta 18 millones considerando valor de reventa',
            '¿Vale la pena comprar un Toyota Yaris nuevo o mejor uno usado del año pasado?',
            '¿Qué SUV tiene mejor valor de reventa entre Hyundai Tucson, Kia Sportage y Toyota RAV4?',
            'Tengo 15 millones, ¿hatchback nuevo o sedán usado? Uso ciudad',
        ],
        typingPhrases: [
            'Consultando precios de nuevos y usados…',
            'Analizando valor de reventa…',
            'Comparando especificaciones técnicas…',
            'Preparando recomendación…',
        ],
    },
    pm: {
        label: 'Product Manager',
        icono: '📊',
        titulo: 'Analista de Mercado — PM',
        subtitulo: 'Inteligencia competitiva · Posicionamiento · Pricing strategy',
        emptyTitulo: '¿Qué necesitas analizar?',
        emptySub: 'Pregunta por posicionamiento de precio, análisis competitivo, gaps de mercado o estrategia de pricing. Uso el framework completo de análisis estratégico.',
        placeholder: 'Ej: Analiza el posicionamiento de precio de los SUV compactos',
        sugerencias: [
            'Analiza el posicionamiento de precio de los SUV compactos',
            '¿Qué marca tiene el pricing más agresivo en sedanes?',
            'Dame gaps y oportunidades de mercado en el segmento eléctrico',
            'Compara Toyota vs Hyundai vs Kia en su gama SUV',
            '¿Qué marca retiene mejor su valor de reventa?',
        ],
        typingPhrases: [
            'Mapeando posicionamiento del segmento…',
            'Analizando competencia…',
            'Calculando valor de reventa…',
            'Preparando recomendaciones estratégicas…',
        ],
    },
};

function TypingIndicator({ frases }) {
    const [phraseIdx, setPhraseIdx] = useState(0);

    useEffect(() => {
        const iv = setInterval(() => {
            setPhraseIdx((p) => (p + 1) % frases.length);
        }, 1800);
        return () => clearInterval(iv);
    }, [frases]);

    return (
        <div className="asesor-msg asesor-msg--ai">
            <div className="asesor-avatar">🤖</div>
            <div className="asesor-bubble asesor-bubble--typing">
                <span className="typing-dot" />
                <span className="typing-dot" />
                <span className="typing-dot" />
                <span className="typing-phrase">{frases[phraseIdx]}</span>
            </div>
        </div>
    );
}

function MetaBadges({ presupuesto, segmento, marcas, latency, tokens }) {
    if (!presupuesto && !segmento && !marcas?.length && !latency && !tokens) return null;
    return (
        <div className="asesor-meta-badges">
            {presupuesto && (
                <span className="asesor-badge asesor-badge--budget">
                    💰 ${Number(presupuesto).toLocaleString('es-CL')}
                </span>
            )}
            {segmento && (
                <span className="asesor-badge asesor-badge--segment">
                    🚗 {segmento}
                </span>
            )}
            {(marcas || []).map((m) => (
                <span key={m} className="asesor-badge asesor-badge--brand">{m}</span>
            ))}
            {latency && (
                <span className="asesor-badge asesor-badge--meta">
                    ⚡ {(latency / 1000).toFixed(1)}s
                </span>
            )}
            {tokens && (
                <span className="asesor-badge asesor-badge--meta">
                    🔢 {tokens} tokens
                </span>
            )}
        </div>
    );
}

export default function AsesorAuto() {
    const { rol } = useUserRole();
    const puedeUsarPM = rol === 'completo' || rol === 'superadmin';

    const [modo, setModo] = useState('asesor');
    // Historial separado por modo — mezclar respuestas de asesor y PM en la
    // misma conversación confundiría al modelo (frameworks/tonos distintos).
    const [historiales, setHistoriales] = useState({ asesor: [], pm: [] });
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const bottomRef = useRef(null);
    const inputRef = useRef(null);
    const uid = auth.currentUser?.uid ?? null;

    const cfg = MODOS[modo];
    const historial = historiales[modo];

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [historial, loading]);

    const cambiarModo = (nuevoModo) => {
        if (nuevoModo === modo || loading) return;
        setModo(nuevoModo);
        setError('');
        setInput('');
    };

    async function enviar(promptOverride) {
        const texto = (promptOverride ?? input).trim();
        if (!texto || loading) return;

        const nuevoMensajeUser = { role: 'user', content: texto };
        const historialActualizado = [...historial, nuevoMensajeUser];

        setHistoriales((prev) => ({ ...prev, [modo]: historialActualizado }));
        setInput('');
        setLoading(true);
        setError('');

        try {
            const historialSinUltimo = historialActualizado.slice(0, -1).map((m) => ({
                role: m.role, content: m.content,
            }));

            if (modo === 'pm') {
                const res = await consultarClaudePM({ uid, prompt: texto, historial: historialSinUltimo });
                const { response, latencyMs, usage } = res.data;
                setHistoriales((prev) => ({
                    ...prev,
                    pm: [...prev.pm, {
                        role: 'assistant', content: response,
                        meta: { latency: latencyMs, tokens: usage ? (usage.input ?? 0) + (usage.output ?? 0) : null },
                    }],
                }));
            } else {
                const res = await consultarAsesorAuto({ uid, prompt: texto, historial: historialSinUltimo });
                const { response, presupuesto, segmento, marcasDetectadas, latencyMs, usage } = res.data;
                setHistoriales((prev) => ({
                    ...prev,
                    asesor: [...prev.asesor, {
                        role: 'assistant', content: response,
                        meta: {
                            presupuesto, segmento, marcas: marcasDetectadas,
                            latency: latencyMs, tokens: usage ? usage.input + usage.output : null,
                        },
                    }],
                }));
            }
        } catch (e) {
            console.error(`Error ${modo === 'pm' ? 'ClaudePM' : 'AsesorAuto'}:`, e);
            setError(e?.message || 'Error al consultar. Intenta de nuevo.');
        } finally {
            setLoading(false);
            inputRef.current?.focus();
        }
    }

    function handleKey(e) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            enviar();
        }
    }

    function limpiarChat() {
        setHistoriales((prev) => ({ ...prev, [modo]: [] }));
        setError('');
        setInput('');
    }

    const chatVacio = historial.length === 0;

    return (
        <main className="asesor-main">

            {/* Header */}
            <div className="asesor-header">
                <div className="asesor-header-left">
                    <div className="asesor-header-icon">{cfg.icono}</div>
                    <div>
                        <h1 className="asesor-title">{cfg.titulo}</h1>
                        <p className="asesor-subtitle">{cfg.subtitulo}</p>
                    </div>
                </div>
                {!chatVacio && (
                    <button className="asesor-btn-clear" onClick={limpiarChat} title="Nueva conversación">
                        ✕ Nueva consulta
                    </button>
                )}
            </div>

            {/* Selector de perspectiva */}
            {puedeUsarPM && (
                <div className="asesor-modo-tabs" role="tablist">
                    {Object.entries(MODOS).map(([key, m]) => (
                        <button
                            key={key}
                            role="tab"
                            aria-selected={modo === key}
                            className={`asesor-modo-tab${modo === key ? ' active' : ''}`}
                            onClick={() => cambiarModo(key)}
                            disabled={loading}
                        >
                            {m.icono} {m.label}
                        </button>
                    ))}
                </div>
            )}

            {/* Chat frame */}
            <div className="asesor-chat-frame">
                {/* Área de chat */}
                <div className="asesor-chat-area">

                    {/* Estado vacío con sugerencias */}
                    {chatVacio && (
                        <div className="asesor-empty">
                            <div className="asesor-empty-icon">🔍</div>
                            <h2 className="asesor-empty-title">{cfg.emptyTitulo}</h2>
                            <p className="asesor-empty-sub">{cfg.emptySub}</p>
                            <div className="asesor-sugerencias">
                                {cfg.sugerencias.map((s, i) => (
                                    <button
                                        key={i}
                                        className="asesor-sugerencia"
                                        onClick={() => enviar(s)}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Mensajes */}
                    {historial.map((msg, i) => (
                        <div
                            key={i}
                            className={`asesor-msg asesor-msg--${msg.role === 'user' ? 'user' : 'ai'}`}
                        >
                            {msg.role === 'assistant' && (
                                <div className="asesor-avatar">🤖</div>
                            )}
                            <div className={`asesor-bubble asesor-bubble--${msg.role === 'user' ? 'user' : 'ai'}`}>
                                {msg.role === 'user' ? (
                                    <p>{msg.content}</p>
                                ) : (
                                    <>
                                        <MetaBadges
                                            presupuesto={msg.meta?.presupuesto}
                                            segmento={msg.meta?.segmento}
                                            marcas={msg.meta?.marcas}
                                            latency={msg.meta?.latency}
                                            tokens={msg.meta?.tokens}
                                        />
                                        <div className="asesor-markdown">
                                            <ReactMarkdown
                                                remarkPlugins={[remarkGfm]}
                                                components={{
                                                    table: ({ node, ...props }) => (
                                                        <div className="asesor-table-wrapper">
                                                            <table className="asesor-table" {...props} />
                                                        </div>
                                                    ),
                                                    thead: ({ node, ...props }) => <thead className="asesor-table-head" {...props} />,
                                                    tbody: ({ node, ...props }) => <tbody className="asesor-table-body" {...props} />,
                                                    tr: ({ node, ...props }) => <tr className="asesor-table-row" {...props} />,
                                                    th: ({ node, ...props }) => <th className="asesor-table-th" {...props} />,
                                                    td: ({ node, ...props }) => <td className="asesor-table-td" {...props} />,
                                                }}
                                            >
                                                {msg.content}
                                            </ReactMarkdown>
                                        </div>
                                    </>
                                )}
                            </div>
                            {msg.role === 'user' && (
                                <div className="asesor-avatar asesor-avatar--user">
                                    {auth.currentUser?.photoURL
                                        ? <img src={auth.currentUser.photoURL} alt="tú" />
                                        : '👤'}
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Indicador de escritura */}
                    {loading && <TypingIndicator frases={cfg.typingPhrases} />}

                    {/* Error */}
                    {error && (
                        <div className="asesor-error">
                            ⚠️ {error}
                        </div>
                    )}

                    <div ref={bottomRef} />
                </div>

                {/* Input */}
                <div className="asesor-input-area">
                    <div className="asesor-input-wrapper">
                        <textarea
                            ref={inputRef}
                            className="asesor-input"
                            placeholder={cfg.placeholder}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKey}
                            rows={2}
                            disabled={loading}
                        />
                        <button
                            className={`asesor-send ${loading ? 'asesor-send--loading' : ''} ${input.trim() && !loading ? 'asesor-send--ready' : ''}`}
                            onClick={() => enviar()}
                            disabled={loading || !input.trim()}
                            aria-label="Enviar"
                        >
                            {loading ? '⏳' : '➤'}
                        </button>
                    </div>
                    <p className="asesor-input-hint">
                        Shift + Enter para nueva línea · Enter para enviar
                    </p>
                </div>
            </div>{/* /asesor-chat-frame */}

        </main>
    );
}
